import { DateTime as DT } from 'luxon';
import { safeJSONParse } from 'utils/helpers/safeParsing';
import { getPresetPeriodRange, periodRangeToMillis, type ReportPeriodType } from '../ReportDate/periodUtils';
import type { ReportVatFilter } from '../types';

const STORAGE_KEY = 'reportObjects.filters';

export interface ReportObjectsFiltersState {
    period: ReportPeriodType;
    dates: [number, number];
    customRange: [number, number] | null;
    vat: ReportVatFilter;
    includeObjects: boolean;
    includeSales: boolean;
}

export interface ReportDateFilterState {
    period: ReportPeriodType;
    dates: [number, number] | null;
    customRange: [number, number] | null;
}

export const createOffDateFilter = (): ReportDateFilterState => ({
    period: 'off',
    dates: null,
    customRange: null,
});

const createDefaultFilters = (): ReportObjectsFiltersState => {
    const { start, end } = getPresetPeriodRange('month', DT.now());
    return {
        period: 'month',
        dates: periodRangeToMillis(start, end),
        customRange: null,
        vat: null,
        includeObjects: true,
        includeSales: true,
    };
};

const parseBoolDefaultTrue = (value: unknown): boolean => {
    if (value === false || value === 0 || value === '0' || value === 'false') return false;
    return true;
};

const parseVat = (value: unknown): ReportVatFilter => {
    if (value === 0 || value === '0') return 0;
    if (value === 1 || value === '1') return 1;
    return null;
};

export const loadReportObjectsFilters = (): ReportObjectsFiltersState => {
    const saved = safeJSONParse(sessionStorage.getItem(STORAGE_KEY)) as Partial<ReportObjectsFiltersState> | null;
    if (!saved?.dates?.[0] || !saved?.dates?.[1]) {
        return createDefaultFilters();
    }

    const period: ReportPeriodType =
        saved.period === 'month' || saved.period === 'quarter' || saved.period === 'year' || saved.period === 'custom'
            ? saved.period
            : 'month';

    return {
        period,
        dates: [saved.dates[0], saved.dates[1]],
        customRange:
            saved.customRange?.[0] && saved.customRange?.[1]
                ? [saved.customRange[0], saved.customRange[1]]
                : null,
        vat: parseVat(saved.vat),
        includeObjects: parseBoolDefaultTrue(saved.includeObjects),
        includeSales: parseBoolDefaultTrue(saved.includeSales),
    };
};

export const saveReportObjectsFilters = (state: ReportObjectsFiltersState): void => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
};
